import * as React from "react";

export type Pos = { x: number; y: number };

function readPositions(storageKey: string): Record<string, Pos> {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function useIslandPositions(
  storageKey = "islandPositions_v1",
  scatter = false,
  useStorage = true
) {
  const [positions, setPositions] = React.useState<Record<string, Pos>>(() => {
    if (!useStorage) return {};
    return readPositions(storageKey);
  });

  // ✅ stable function + also clears localStorage key for cleanliness
  const clearAllPositions = React.useCallback(() => {
    setPositions({});
    if (!useStorage) return;
    try {
      localStorage.removeItem(storageKey);
    } catch {}
  }, [storageKey, useStorage]);

  // ✅ reload when storageKey/useStorage changes (user switch, orientation switch, etc.)
  React.useEffect(() => {
    if (!useStorage) {
      setPositions({});
      return;
    }
    setPositions(readPositions(storageKey));
  }, [storageKey, useStorage]);

  // Persist to localStorage (only when enabled)
  React.useEffect(() => {
    if (!useStorage) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(positions));
    } catch {}
  }, [positions, storageKey, useStorage]);

  const getPos = (key: string, dx = 0, dy = 0): Pos => {
    const saved = positions[key];
    if (saved) return saved;
    return scatter ? { x: dx, y: dy } : { x: 0, y: 0 };
  };

  const setPosition = (key: string, x: number, y: number) =>
    setPositions((p) => ({ ...p, [key]: { x, y } }));

  React.useEffect(() => {
    const handleResize = () => setPositions((p) => ({ ...p }));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ IMPORTANT: return it
  return { positions, getPos, setPosition, clearAllPositions };
}
